import { Node } from './api/APITypes';
import { Filter, Folder, GraphView, SearchType } from './state/ViewTypes';
import Graph from 'graphology';


export function FilterGraphNodes(
  searchQuery: string,
  graph?: Graph,
  centerNodeId?: string,
  filter?: Filter,
): Node[] {
  if (!graph || graph.order === 0) {
    return [];
  }

  const parsedNodes: Node[] = graph.nodes().map((nodeID) => {
    
    const attrs = graph.getNodeAttributes(nodeID);

    return {
      elementId: nodeID,
      id: attrs.id,
      date: attrs.judgementdate,
      label: attrs.label,
      name: attrs.label,
      url: attrs.url,
      specific_properties: {
        id: attrs.id,
        label: attrs.label,
        appno: attrs.appno,
        chambertype: attrs.chambertype,
        country: attrs.country,
        description: '',
        importance: attrs.importance,
        judgementdate: attrs.judgementdate,
        keywords: attrs.keywords,
        nonviolation: attrs.nonviolation,
        separateopinion: false,
        url: attrs.url,
        violation: attrs.violation,
      },
    };
  });

  const filteredNodes = FilterNodes('GRAPH', searchQuery, parsedNodes, filter);

  const centerNode = parsedNodes.find((e) => e.id === centerNodeId);

  if (centerNode) {
    return Array.from(new Set([...filteredNodes, centerNode]));
  }

  return filteredNodes;
}

export function FilterFolders(folders: Folder[], searchQuery: string): Folder[] {
  if (!folders || folders.length === 0) {
    return [];
  }

  if (searchQuery === '') {
    return folders;
  }
  return folders.filter((e) => e.name.toLowerCase().includes(searchQuery.toLowerCase()));
}

export function FilterGraphViews(graphViews: GraphView[], searchQuery: string): GraphView[] {
  if (!graphViews || graphViews.length === 0) {
    return [];
  }

  if (searchQuery === '') {
    return graphViews;
  }
  return graphViews.filter((e) => e.name.toLowerCase().includes(searchQuery.toLowerCase()));
}

export default function FilterNodes(
  documentSearchType: SearchType,
  searchQuery: string,
  nodes: Node[],
  filter?: Filter,
) {
  
  // 
  if (!nodes || nodes.length === 0) {
    return [];
  }
  
  // 
  let filteredNodes = nodes;

  // search Query
  if (documentSearchType !== 'CONTEXT' && documentSearchType !== 'EXACT') {
    filteredNodes = filteredNodes.filter((node) =>
      node.label?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }
  
  // 
  if (!filter) return filteredNodes;

  // database
  filteredNodes = filteredNodes.filter((node) =>
    Object.keys(filter.source).some((db) => node.id.startsWith(db.toLowerCase())),
  );

  // date
  filteredNodes = filteredNodes.filter(
    (node) =>
      new Date(node.date) > new Date(filter.time.lower) &&
      new Date(node.date) < new Date(filter.time.upper),
  );

  // importance
  filteredNodes = filteredNodes.filter((node) => {
    if (node.specific_properties.importance) {
      return filter.source.hudoc.importance.includes(String(node.specific_properties.importance));
    }
    return true;
  });

  // decision level
  filteredNodes = filteredNodes.filter((node) => {
    if (node.specific_properties.chambertype) {
      return filter.source.hudoc.decisionLevel.includes(node.specific_properties.chambertype);
    }
    return true;
  });
  
  // country
  filteredNodes = filteredNodes.filter((node) => {
    if (node.specific_properties.country) {
      return node.specific_properties.country.some((country) =>
        filter.source.hudoc.countries.includes(country),
      );
    }
    return true;
  });

  // conclusion
  filteredNodes = filteredNodes.filter((node) => {
    const V = node.specific_properties.violation;
    const NV = node.specific_properties.nonviolation;

    const existsV = V !== null && V !== undefined && V !== "";
    const existsNV = NV !== null && NV !== undefined && NV !== "";

    const hasV = existsV ? filter.source.hudoc.conclusion.includes('violation') : true;
    const hasNV = existsNV ? filter.source.hudoc.conclusion.includes('nonviolation') : true;
    
    return hasNV && hasV;
  });
  
  // article
  filteredNodes = filteredNodes.filter((node) => {
    let violationString = node.specific_properties.violation;
    let nonViolationString = node.specific_properties.nonviolation;

    if (!violationString && !nonViolationString) {
      return true;
    }
    
    if (!violationString) violationString = '';
    if (!nonViolationString) nonViolationString = '';
    
    const violation: string[] = violationString.split(';');
    const nonViolation: string[] = nonViolationString.split(';');

    if (violation) {
      for (let article of violation) {
        for (let filterArticle of filter.source.hudoc.articles) {
          let regex = new RegExp(`^${filterArticle}(\\D.*|$)`);
          if (regex.test(article)) return true;
        }
      }
    }
    
    if (nonViolation) {
      for (let article of nonViolation) {
        for (let filterArticle of filter.source.hudoc.articles) {
          let regex = new RegExp(`^${filterArticle}(\\D.*|$)`);
          if (regex.test(article)) return true;
        }
      }
    }

    return false;
  });

  return filteredNodes;
}
